import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import UtmsButton from '@shared/components/ui/UtmsButton';
import { ChangeEvent, Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Booking,
    BookingCalendarSelection,
    BookingDeviceModalData,
    BookingDeviceModalMode,
    BookingFormErrorMessage,
    BookingStatus,
    ClosestBookingRequestParams,
    CreateBookingRequestParams,
    Device,
    SelectedBookingId,
} from '../types';
import TextField from '@mui/material/TextField';
import { useDispatch, useSelector } from 'react-redux';
import {
    BookingState,
    cancelBookingRequest,
    cancelBookingReset,
    createBookingRequest,
    createBookingReset,
    createLocalBooking,
    deleteLocalBooking,
    getClosestBookingRequest,
    getClosestBookingReset,
    selectBooking,
    unselectBooking,
    updateBookingRequest,
    updateBookingReset,
    updateLocalBooking,
} from '../store';
import { utmsButtonSx } from '@shared/constants/otherConstants';
import {
    selectAllBookings,
    selectCancelBookingStatus,
    selectCreateBookingStatus,
    selectUpdateBookingStatus,
} from '../store/selectors';
import LoadingButton from '@mui/lab/LoadingButton';
import { selectCurrentUser } from '@features/auth/store/selectors';
import isEqual from 'lodash/isEqual';
import BookingDeviceModalHeader from './BookingDeviceModalHeader';
import BookingDeviceModalBookingView from './BookingDeviceModalBookingView';
import BookingDeviceModalForm from './BookingDeviceModalForm';
import BookingCredentials from './BookingCredentials';
import BookingCredentialsPlaceholder from './BookingCredentialsPlaceholder';
import { AccessEnum, RequestStatusEnum } from '@shared/types';
import {
    generateSlotsIndexesRange,
    getBookingDateRangeFromDatesDifference,
    isPastCalendarItem,
    useIsBookingSelected,
} from '@pages/booking/utils';
import { uuidv4 } from '@shared/utils/utilsOld';
import { getDatesDifferenceFromDateRange } from '@shared/utils/utils';
import AccessSelector from '@shared/components/others/AccessSelector/AccessSelector';
import { useBookingConfigContext } from '@pages/booking/contexts/BookingConfigContext';
import { MIN_DAYS_BOOKING, MIN_HOURS_BOOKING } from '@pages/booking/constants';

interface BookingDeviceModalProps {
    modalData: BookingDeviceModalData;
    setModalData: Dispatch<SetStateAction<BookingDeviceModalData | null>>;
    setBookingsSelection: Dispatch<SetStateAction<BookingCalendarSelection>>;
    setSelectedBookingId: (newBookingId: SelectedBookingId) => void;
    selectedDevice?: Device;
}

const BookingDeviceModal: FC<BookingDeviceModalProps> = ({
    modalData,
    setModalData,
    setBookingsSelection,
    selectedDevice,
    setSelectedBookingId,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { localBookingsStorageMode, selectMode } = useBookingConfigContext();
    const { open, bookingForItem, dateTime, slotIndex, mode } = modalData;
    const [daysCount, setDaysCount] = useState(MIN_DAYS_BOOKING);
    const [hoursCount, setHoursCount] = useState(MIN_HOURS_BOOKING);
    const [slotsCount, setSlotsCount] = useState(slotIndex && mode === BookingDeviceModalMode.MANAGE ? 0 : 1); // 0 because setInitialBookingParams will init value
    const [comment, setComment] = useState(bookingForItem?.comment || '');
    const [access, setAccess] = useState(bookingForItem?.access || AccessEnum.Private);
    // const [shouldChangePassword, setShouldChangePassword] = useState(true);
    // const [shouldDisablePubKey, setShouldDisablePubKey] = useState(true);
    const createBookingStatus = useSelector(selectCreateBookingStatus);
    const updateBookingStatus = useSelector(selectUpdateBookingStatus);
    const cancelBookingStatus = useSelector(selectCancelBookingStatus);
    const isBookingSelected = useIsBookingSelected({ bookingForItem, selectedDevice });
    const bookings = useSelector((state: { booking: BookingState }) => selectAllBookings(state, selectedDevice?.pk));
    const currentUser = useSelector(selectCurrentUser);
    const [dateRangeErrorMessage, setDateRangeErrorMessage] = useState<BookingFormErrorMessage>({
        message: '',
        field: null,
    });
    const dateTimeISO = dateTime.toISOString();
    const isBookingInPast = bookingForItem !== null && isPastCalendarItem(bookingForItem.booked_to);
    const isInProgress =
        bookingForItem !== null &&
        isPastCalendarItem(bookingForItem.booked_from) &&
        !isPastCalendarItem(bookingForItem.booked_to) &&
        [BookingStatus.IN_PROGRESS, BookingStatus.PENDING].includes(bookingForItem.status);
    const interactionDisabled = [createBookingStatus, updateBookingStatus, cancelBookingStatus].includes(
        RequestStatusEnum.Loading,
    );

    useEffect(() => {
        // if booking was updated in array, update modal data too

        if (bookings.length && bookingForItem) {
            const bookingForItemFromArray = bookings.find((booking) => booking.pk === bookingForItem?.pk);

            if (bookingForItemFromArray && !isEqual(bookingForItem, bookingForItemFromArray)) {
                setModalData((prev: BookingDeviceModalData | null) =>
                    prev
                        ? {
                              ...prev,
                              bookingForItem: bookingForItemFromArray,
                          }
                        : null,
                );
            }
        }
    }, [bookings, bookingForItem, setModalData]);

    const isBookingModified = useMemo(() => {
        if (mode !== BookingDeviceModalMode.MANAGE || !bookingForItem) {
            return;
        }

        const [initialDaysCount, initialHoursCount] = getDatesDifferenceFromDateRange(
            bookingForItem.booked_from,
            bookingForItem.booked_to,
        );
        const initialSlotsCount = bookingForItem?.slots?.length;

        return (
            !(daysCount === initialDaysCount && hoursCount === initialHoursCount) ||
            (slotIndex !== undefined && initialSlotsCount !== slotsCount) ||
            comment !== bookingForItem.comment ||
            access !== bookingForItem.access
        );
    }, [mode, bookingForItem, daysCount, hoursCount, slotIndex, slotsCount, comment, access]);

    const bookingStartDate = useMemo(() => {
        return mode === BookingDeviceModalMode.CREATE ? dateTime : new Date((bookingForItem as Booking).booked_from);
    }, [bookingForItem, dateTime, mode]);

    const [futureBookingFrom, futureBookingTo] = useMemo(() => {
        if (mode === BookingDeviceModalMode.VIEW) {
            return [];
        }

        return getBookingDateRangeFromDatesDifference(daysCount, hoursCount, bookingStartDate);
    }, [bookingStartDate, daysCount, hoursCount, mode]);

    const setInitialBookingParams = useCallback(() => {
        if (!bookingForItem) {
            return;
        }

        if ([BookingDeviceModalMode.MANAGE, BookingDeviceModalMode.CREATE].includes(mode)) {
            const [days, hours] = getDatesDifferenceFromDateRange(bookingForItem.booked_from, bookingForItem.booked_to);
            setDaysCount(days);
            setHoursCount(hours);

            const slotsCount = bookingForItem?.slots?.length;

            if (slotsCount) {
                setSlotsCount(slotsCount);
            }

            setComment(bookingForItem.comment);
            setAccess(bookingForItem.access);
        }
    }, [bookingForItem, mode]);

    useEffect(() => {
        setInitialBookingParams();
    }, [setInitialBookingParams]);

    const handleClose = useCallback(() => {
        setModalData(null);
        setInitialBookingParams();
        setBookingsSelection({}); // Reset visible selection when close modal
        setDateRangeErrorMessage({ field: null, message: '' });

        setSelectedBookingId(null);
    }, [setBookingsSelection, setInitialBookingParams, setModalData, setSelectedBookingId]);

    useEffect(() => {
        if (!selectedDevice || mode === BookingDeviceModalMode.VIEW || isBookingInPast) {
            return;
        }

        if (open) {
            const requestParams: ClosestBookingRequestParams = { device: selectedDevice.pk, from: dateTimeISO };

            if (slotIndex) {
                if (!slotsCount) {
                    return;
                }

                requestParams.slots = generateSlotsIndexesRange(slotIndex, slotsCount);
            }

            dispatch(getClosestBookingRequest(requestParams));
        } else {
            dispatch(getClosestBookingReset());
        }
    }, [dispatch, open, selectedDevice, mode, dateTimeISO, slotsCount, slotIndex, isBookingInPast]);

    useEffect(() => {
        if (
            createBookingStatus === RequestStatusEnum.Success ||
            updateBookingStatus === RequestStatusEnum.Success ||
            cancelBookingStatus === RequestStatusEnum.Success
        ) {
            handleClose();
            dispatch(createBookingReset());
            dispatch(updateBookingReset());
            dispatch(cancelBookingReset());
        }
    }, [createBookingStatus, updateBookingStatus, cancelBookingStatus, dispatch, handleClose]);

    const handleCommentChange = (e: ChangeEvent<HTMLInputElement>) => {
        setComment(e.target.value);
    };

    const handleAccessChange = (newAccess: AccessEnum) => {
        setAccess(newAccess);
    };

    // const handleCheckboxChange =
    //     (setStateFn: Dispatch<SetStateAction<boolean>>) => (event: ChangeEvent<HTMLInputElement>) => {
    //         setStateFn(event.target.checked);
    //     };

    const handleCreateBooking = () => {
        if (!selectedDevice || !futureBookingFrom || !futureBookingTo) {
            return;
        }

        const slotsIndexes = slotIndex !== undefined ? generateSlotsIndexesRange(slotIndex, slotsCount) : undefined;
        const bookingData = {
            device: selectedDevice.pk,
            booked_from: futureBookingFrom,
            booked_to: futureBookingTo,
            // should_change_password: shouldChangePassword,
            // should_disable_pubkey: shouldDisablePubKey,
            slots: slotsIndexes || undefined,
            comment: comment,
            access: access,
        } satisfies Partial<CreateBookingRequestParams>;

        if (localBookingsStorageMode) {
            const localGeneratedBookingFields = {
                pk: uuidv4(),
                status: BookingStatus.PENDING,
                created_by_user: currentUser,
                isLocal: true,
            } satisfies Partial<Booking>;
            const fullBookingData = { ...bookingData, ...localGeneratedBookingFields };

            dispatch(
                createLocalBooking({
                    deviceId: selectedDevice.pk,
                    booking: fullBookingData,
                }),
            );
            selectMode &&
                dispatch(
                    selectBooking({
                        deviceId: selectedDevice.pk,
                        booking: fullBookingData,
                    }),
                );
            handleClose();
        } else {
            dispatch(createBookingRequest({ ...bookingData, selectMode }));
        }
    };

    const handleUpdateBooking = () => {
        if (!bookingForItem || !futureBookingTo || !selectedDevice) {
            return;
        }

        const slotsIndexes = slotIndex !== undefined ? generateSlotsIndexesRange(slotIndex, slotsCount) : undefined;
        const bookingDataToUpdate = {
            booked_to: futureBookingTo,
            slots: slotsIndexes || undefined,
            comment: comment,
            access: access,
        };
        const fullBookingData = { ...bookingForItem, ...bookingDataToUpdate };

        if (bookingForItem.isLocal) {
            dispatch(
                updateLocalBooking({
                    deviceId: selectedDevice.pk,
                    booking: fullBookingData,
                }),
            );
            handleClose();
        } else {
            dispatch(
                updateBookingRequest({
                    bookingId: bookingForItem.pk,
                    ...bookingDataToUpdate,
                }),
            );
        }

        if (selectMode) {
            dispatch(
                selectBooking({
                    deviceId: selectedDevice.pk,
                    booking: fullBookingData,
                }),
            );
        }
    };

    const handleCancelBooking = () => {
        if (!bookingForItem) {
            return;
        }

        if (bookingForItem.isLocal) {
            selectedDevice &&
                dispatch(deleteLocalBooking({ deviceId: selectedDevice.pk, bookingId: bookingForItem.pk }));
            handleClose();
        } else {
            dispatch(
                cancelBookingRequest({
                    bookingId: bookingForItem.pk,
                    isInProgress: isInProgress,
                }),
            );
        }
    };

    const handleSelectBooking = () => {
        if (!bookingForItem || !selectedDevice) {
            return;
        }

        dispatch(
            selectBooking({
                deviceId: selectedDevice.pk,
                booking: bookingForItem,
            }),
        );
        handleClose();
    };

    const handleUnselectBooking = () => {
        if (!selectedDevice) {
            return;
        }

        dispatch(
            unselectBooking({
                deviceId: selectedDevice.pk,
            }),
        );
        handleClose();
    };

    return (
        <Dialog fullWidth maxWidth="xs" open={open} onClose={interactionDisabled ? undefined : handleClose}>
            <BookingDeviceModalHeader
                mode={mode}
                selectedDevice={selectedDevice}
                futureBookingFrom={futureBookingFrom}
                futureBookingTo={futureBookingTo}
            />
            <DialogContent sx={{ padding: '16px 24px', display: 'flex', flexDirection: 'column' }}>
                {mode === BookingDeviceModalMode.VIEW && bookingForItem && (
                    <BookingDeviceModalBookingView bookingForItem={bookingForItem} />
                )}
                {
                    <>
                        {!localBookingsStorageMode &&
                            selectedDevice &&
                            bookingForItem &&
                            (bookingForItem.status === BookingStatus.IN_PROGRESS && bookingForItem.entry_point ? (
                                <BookingCredentials selectedDevice={selectedDevice} bookingForItem={bookingForItem} />
                            ) : (
                                <BookingCredentialsPlaceholder
                                    bookingForItem={bookingForItem}
                                    isInProgress={isInProgress}
                                />
                            ))}
                        {[BookingDeviceModalMode.MANAGE, BookingDeviceModalMode.CREATE].includes(mode) && (
                            <BookingDeviceModalForm
                                mode={mode}
                                bookingForItem={bookingForItem}
                                daysCount={daysCount}
                                hoursCount={hoursCount}
                                slotsCount={slotsCount}
                                dateRangeErrorMessage={dateRangeErrorMessage}
                                setDaysCount={setDaysCount}
                                setHoursCount={setHoursCount}
                                setSlotsCount={setSlotsCount}
                                setBookingsSelection={setBookingsSelection}
                                setDateRangeErrorMessage={setDateRangeErrorMessage}
                                bookingStartDate={bookingStartDate}
                                isBookingInPast={isBookingInPast}
                                futureBookingFrom={futureBookingFrom}
                                futureBookingTo={futureBookingTo}
                                slotIndex={slotIndex}
                                selectedDevice={selectedDevice}
                            />
                        )}
                    </>
                }
                <TextField
                    sx={{ mt: 2, pointerEvents: mode === BookingDeviceModalMode.VIEW ? 'none' : 'initial' }}
                    margin="dense"
                    label={t('comment')}
                    placeholder={t('enter_comment_for_booking')}
                    multiline
                    minRows={2}
                    maxRows={4}
                    fullWidth
                    value={comment}
                    onChange={handleCommentChange}
                    slotProps={{
                        input: {
                            readOnly: mode === BookingDeviceModalMode.VIEW,
                        },
                    }}
                />
                {mode !== BookingDeviceModalMode.VIEW && (
                    <AccessSelector margin="dense" sx={{ mt: 2 }} data={access} setData={handleAccessChange} withHint />
                )}
                {/* {mode === BookingDeviceModalMode.CREATE && (
                    <>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={shouldChangePassword}
                                    onChange={handleCheckboxChange(setShouldChangePassword)}
                                />
                            }
                            label={t('should_change_password')}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={shouldDisablePubKey}
                                    onChange={handleCheckboxChange(setShouldDisablePubKey)}
                                />
                            }
                            label={t('should_disable_pubkey')}
                        />
                    </>
                )} */}
            </DialogContent>
            <DialogActions>
                {mode === BookingDeviceModalMode.CREATE && (
                    <LoadingButton
                        sx={utmsButtonSx}
                        disabled={Boolean(dateRangeErrorMessage.field)}
                        loading={createBookingStatus === RequestStatusEnum.Loading}
                        onClick={handleCreateBooking}
                    >
                        {selectMode ? t('book_and_select') : t('book')}
                    </LoadingButton>
                )}
                {mode === BookingDeviceModalMode.MANAGE &&
                    (isBookingModified ? (
                        <LoadingButton
                            sx={utmsButtonSx}
                            disabled={Boolean(dateRangeErrorMessage.field)}
                            loading={updateBookingStatus === RequestStatusEnum.Loading}
                            onClick={handleUpdateBooking}
                        >
                            {selectMode && !isBookingSelected ? t('save_and_select') : t('save')}
                        </LoadingButton>
                    ) : (
                        !isBookingInPast && (
                            <>
                                <LoadingButton
                                    sx={utmsButtonSx}
                                    loading={cancelBookingStatus === RequestStatusEnum.Loading}
                                    onClick={handleCancelBooking}
                                    color="error"
                                >
                                    {t('cancel_book')}
                                </LoadingButton>
                                {selectMode && (
                                    <UtmsButton
                                        disabled={!isBookingSelected && Boolean(dateRangeErrorMessage.field)}
                                        onClick={isBookingSelected ? handleUnselectBooking : handleSelectBooking}
                                    >
                                        {isBookingSelected ? t('unselect') : t('select_r')}
                                    </UtmsButton>
                                )}
                            </>
                        )
                    ))}
                <UtmsButton disabled={interactionDisabled} color="secondary" onClick={handleClose}>
                    {t('close')}
                </UtmsButton>
            </DialogActions>
        </Dialog>
    );
};

export default BookingDeviceModal;
